// Email notifications tab — which automated emails this person wants to receive.
import { useEffect, useMemo, useState } from "react";
import { BellRing, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

export function NotificationPrefsTab() {
  const [items, setItems] = useState(null);
  const [outs, setOuts] = useState(new Set());
  const [saved, setSaved] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/report-hub/notification-prefs").then((r) => {
      setItems(r.data.items || []);
      const s = new Set(r.data.optouts || []);
      setOuts(s); setSaved(new Set(s));
    }).catch(() => setItems([]));
  }, []);

  const groups = useMemo(() => {
    const g = [];
    (items || []).forEach((it) => {
      let grp = g.find((x) => x.label === it.group);
      if (!grp) { grp = { label: it.group, items: [] }; g.push(grp); }
      grp.items.push(it);
    });
    return g;
  }, [items]);

  const dirty = outs.size !== saved.size || [...outs].some((k) => !saved.has(k));
  const toggle = (key, on) => setOuts((prev) => { const n = new Set(prev); if (on) n.delete(key); else n.add(key); return n; });
  const setGroup = (grp, on) => setOuts((prev) => { const n = new Set(prev); grp.items.forEach((it) => (on ? n.delete(it.key) : n.add(it.key))); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/report-hub/notification-prefs", { optouts: [...outs] });
      const s = new Set(data.optouts || []);
      setOuts(s); setSaved(new Set(s));
      toast.success(s.size ? `Saved — ${s.size} notification${s.size === 1 ? "" : "s"} switched off` : "Saved — you'll receive every automated email");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  if (items === null) return <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="notification-prefs-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">
          These are the automated emails the platform can send you. Switch off anything you don't want in your inbox —
          the in-app bell keeps working either way. Transactional mail (invites, password resets, quotes you send) is always delivered.
        </p>
        <Button onClick={save} disabled={!dirty || saving} data-testid="prefs-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save choices
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {groups.map((grp) => {
          const allOn = grp.items.every((it) => !outs.has(it.key));
          return (
            <Card key={grp.label} className="border-slate-200 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid={`prefs-group-${grp.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100"><BellRing className="mr-1.5 inline h-4 w-4 text-primary" />{grp.label}</h3>
                <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => setGroup(grp, !allOn)} data-testid="prefs-group-toggle">
                  {allOn ? "Switch all off" : "Switch all on"}
                </button>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {grp.items.map((it) => (
                  <label key={it.key} className="flex cursor-pointer items-start justify-between gap-3 py-2.5" data-testid={`pref-${it.key}`}>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">{it.label}</span>
                      <span className="block text-xs text-slate-500">{it.sends_when}</span>
                    </span>
                    <Switch checked={!outs.has(it.key)} onCheckedChange={(v) => toggle(it.key, v)} data-testid={`pref-switch-${it.key}`} />
                  </label>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
